import { useTranslation } from "react-i18next";
import { useGlobal } from "context/GlobalContext";
import { useToast } from "context/ToastContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import * as apiWarehouse from "api/apiWarehouses";
import { useListRefreshEffect } from "utils/helpers/list/useListRefreshEffect";
import { useListOnPage } from "utils/helpers/list/useListOnPage";
import ESpinner from "../others/Spinner";
import { Card, Col, Row } from "react-bootstrap";
import vconst from "utils/helpers/structs/vconst";
import WarehouseToolBar from "./WarehouseToolBar";
import { useWarehouse } from "./context/WarehouseProvider";
import WarehouseTable from "./WarehouseTable";
import OffComing from "./OffComing";
import OffTransfer from "./OffTransfer";
import OffWarehouseOperations from "./OffWarehouseOperations";
import { format$Price } from "utils/helpers/functions/formatPrice";
import TitleCounter from "../others/TitleCounter";
import OffFilter from "./OffFilter";
import { useAuth } from "context/AuthContext";
import { canWarehouseOps } from "utils/helpers/access/hasUserAccess";
import { useSettings } from "context/SettingsContext";
import OffPayments from "./OffPayments";
  
const Warehouse = ({ wId }: { wId: number }) => {
    const { t } = useTranslation(['Warehouses']);
    const { g } = useGlobal();
    const { auth } = useAuth();
    const { getSettings } = useSettings();
    const { showToast } = useToast();
    const { stocksOrder, stocksDesc, showSpinner, setShowSpinner, stockFilter, setStockFilter, showCredit, setStocksOrder, setStocksDesc, listResetNonce, listRefreshNonce } = useWarehouse();
    const [ stocksList, setStocksList ] = useState<any[] | null>(null);
    const [ warehouseInfo, setWarehouseInfo ] = useState<any>(null);
    const [ page, setPage ] = useState<number | null>(null);
    const [ showComing, setShowComing ] = useState<boolean | number>(false) 
    const [ showTransfer, setShowTransfer ] = useState<boolean | number | 'all'>(false) 
    const [ showFilter, setShowFilter ] = useState(false) 
    const [ balance, setBalance ] = useState(0);
    const [ credit, setCredit ] = useState(0);
    const [ teamId, setTeamId ] = useState<number | null>(null);
    const [ teamName, setTeamName ] = useState<string>('');
    const [ teamDeleted, setTeamDeleted ] = useState(false);
    const [ customId, setCustomId ] = useState<number | null>(null);
    const [ customName, setCustomName ] = useState<string>('');
    const [ customDeleted, setCustomDeleted ] = useState(false);
    const [ warehouseEmpty, setWarehouseEmpty ] = useState<boolean | null>(null);
    const location = useLocation();
    const onPage = useListOnPage();
    const prevStockData = useRef<string | null>(null);
    const [ prevFilter, setPrevFilter ] = useState<Record<string, any> | null>(stockFilter);
    const [ showPaymentsId, setShowPaymentsId ] = useState<number | null>(null);
    const [ showPaymentsTitle, setShowPaymentsTitle ] = useState<string | null>(null);

    const canOps = canWarehouseOps(auth?.user);

    const fetchStocks = async (force=false) => {
        if (force) prevStockData.current = null;
        if ((!page) || (showSpinner)) return null;
        
        const search = `%${g?.search?.[location.pathname]||''}%`;
        const stockData = {
            search  : search, 
            page    : (page < 0) ? 1 : page||1,
            onPage,
            order   : `${stocksOrder ? `order by ${stocksOrder} ` : ''} ${stocksDesc ? 'DESC' : ''}`,
            ...stockFilter

        }
 
        // Запобіжник для подвійного запиту
        if (prevStockData.current == JSON.stringify({...stockData,wId})) return;
        setShowSpinner(true);
        const resStock = await apiWarehouse.getStocks(wId, stockData);
        setShowSpinner(false);
        if (!resStock) return showToast(t('common:messError'), 'danger');
        setStocksList(resStock?.stocks);
        setBalance(Number(resStock?.balance) || 0);
        setCredit(Number(resStock?.credit) || 0);
        setWarehouseInfo(resStock?.warehouse);
        setTeamId(resStock?.warehouse?.team?.ID || null);
        setTeamName(resStock?.warehouse?.team?.teamName || '');
        setTeamDeleted(!!Number(resStock?.warehouse?.team?.deleted));
        setCustomId(resStock?.warehouse?.custom?.ID || resStock?.warehouse?.customId || null);
        setCustomName(resStock?.warehouse?.custom?.name || '');
        setCustomDeleted(!!Number(resStock?.warehouse?.custom?.deleted));
        setWarehouseEmpty(resStock?.warehouseEmpty ?? null);
        g.setPaginator((prev: any) =>({
            ...prev,
            [location.pathname] : resStock.paginator
        })); 

    }

    const resetStocksList = useCallback(async () => {
        prevStockData.current = null;
        setStocksOrder('');
        setStocksDesc(false);
        setStockFilter(null);
        setPrevFilter(null);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname]||''}%`;
        setShowSpinner(true);
        const resStock = await apiWarehouse.getStocks(wId, {
            search,
            page: 1,
            onPage,
            order: '',
        });
        setShowSpinner(false);
        if (!resStock) return showToast(t('common:messError'), 'danger');
        setStocksList(resStock?.stocks);
        setBalance(Number(resStock?.balance) || 0);
        setCredit(Number(resStock?.credit) || 0);
        setWarehouseInfo(resStock?.warehouse);
        setTeamId(resStock?.warehouse?.team?.ID || null);
        setTeamName(resStock?.warehouse?.team?.teamName || '');
        setTeamDeleted(!!Number(resStock?.warehouse?.team?.deleted));
        setCustomId(resStock?.warehouse?.custom?.ID || resStock?.warehouse?.customId || null);
        setCustomName(resStock?.warehouse?.custom?.name || '');
        setCustomDeleted(!!Number(resStock?.warehouse?.custom?.deleted));
        setWarehouseEmpty(resStock?.warehouseEmpty ?? null);
        g.setPaginator((prev: any) =>({
            ...prev,
            [location.pathname] : resStock.paginator
        }));
    }, [g, location.pathname, onPage, setShowSpinner, setStockFilter, setStocksDesc, setStocksOrder, showToast, t, wId]);

    useListRefreshEffect(listResetNonce, () => {
        void resetStocksList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevStockData.current = null;
        void fetchStocks(true);
    });
    
    // Хук на пошук
    useEffect(()=>{
        if (page == 1) fetchStocks();
        else setPage(1)
    }, [g.search?.[location.pathname], g.target]);

    // Змінюємо "внутрішню сторінку" при зміні пагінатора 
    useEffect(()=>{
        const _page = g?.paginator?.[location.pathname]?.currentPage||1;
        if (page != _page) 
            setPage(_page);
    }, [g?.paginator?.[location.pathname]?.currentPage, 
        g?.paginator?.[location.pathname]?.countRows,
        g?.paginator?.[location.pathname]?.onPage]);

    // Хук на зміну сторінки
    useEffect(()=>{ 
        fetchStocks();
    }, [page,wId,stocksOrder,stocksDesc,stockFilter,onPage]);   


 
    const handleTeamChanged = async () => {
        await getSettings();
        await fetchStocks(true);
    };

    const warehouseHeader = (() => {
        if (wId === vconst.waherouseMainId) {
            return {
                type: t('typeMainWarehouse'),
                typeClass: 'text-info',
                badgeClass: null as string | null,
                name: null as string | null,
            };
        }
        if (wId === vconst.waherouseWriteOffId) {
            return {
                type: t('writeOffWarehouse'),
                typeClass: 'text-danger',
                badgeClass: null as string | null,
                name: null as string | null,
            };
        }
        if (!warehouseInfo) {
            return {
                type: t('warehouse'),
                typeClass: 'text-muted',
                badgeClass: null as string | null,
                name: null as string | null,
            };
        }
        if (warehouseInfo.reservObjectId) {
            return {
                type: t('warehouseFilterReserve'),
                typeClass: 'text-warning',
                badgeClass: 'bg-light-warning text-warning',
                name: warehouseInfo.object?.objectName
                    || warehouseInfo.object?.clientName
                    || null,
            };
        }
        if (warehouseInfo.custom?.ID || warehouseInfo.customId || customId) {
            return {
                type: t('typeCustomWarehouse'),
                typeClass: 'text-info',
                badgeClass: 'bg-light-info text-info',
                name: warehouseInfo.custom?.name || customName || null,
            };
        }
        if (warehouseInfo.team?.ID || teamId) {
            return {
                type: t('teamWarehouse'),
                typeClass: 'text-primary',
                badgeClass: 'bg-light-primary text-primary',
                name: warehouseInfo.team?.teamName || teamName || null,
            };
        }
        return {
            type: t('typeObjectWarehouse'),
            typeClass: 'text-success',
            badgeClass: null as string | null,
            name: warehouseInfo.object?.objectName
                || warehouseInfo.object?.clientName
                || null,
        };
    })();

    return (<>
        <OffWarehouseOperations/> 

        <OffFilter
            wId={wId}
            show={showFilter}
            setShow={setShowFilter}
            setPrevFilter={setPrevFilter}
            prevFilter={prevFilter}/>

        <OffPayments
            showTitle={showPaymentsTitle}
            wId={wId}
            showId={showPaymentsId}
            setShowId={setShowPaymentsId}
            fetchStocks={fetchStocks}/>
 
        <OffComing
            fetchStocks={fetchStocks}
            show={showComing}
            setShow={setShowComing}/>

        <OffTransfer
            wId={wId}  
            teamId={teamId}
            reservObjectId={warehouseInfo?.reservObjectId || null}
            fetchStocks={fetchStocks}
            show={showTransfer}
            setShow={setShowTransfer}/> 
 
        <Row>  
            <Col className="col-sticky" sm={12}>
                <div className="w-100 d-flex flex-column flex-xl-row pt-1 justify-content-between align-items-start gap-2 gap-xl-0">
                    <div className="warehouse-header-meta">
                        <div className="warehouse-header-meta__title-row">
                            <h4
                                className="warehouse-header-meta__title text-truncate"
                                title={warehouseHeader.name || warehouseHeader.type || undefined}
                            >
                                {warehouseHeader.name || warehouseHeader.type}
                            </h4>
                            {warehouseHeader.badgeClass && (
                                <span className={`badge fs-12 ${warehouseHeader.badgeClass}`}>
                                    {warehouseHeader.type}
                                </span>
                            )}
                        </div>
                        <div className="warehouse-header-meta__line">
                            {warehouseHeader.name && !warehouseHeader.badgeClass && (
                                <span className={`warehouse-header-meta__type ${warehouseHeader.typeClass}`}>
                                    {warehouseHeader.type}
                                </span>
                            )}
                            <TitleCounter
                                title={t('balance')}
                                count={format$Price(balance || 0)}
                            />
                            {showCredit && auth?.user?.access?.role == 'manager' && wId === vconst.waherouseMainId && (
                                <TitleCounter
                                    color={credit == 0 ? 'success' : credit > 0 ? 'danger' : 'warning'}
                                    title={credit == 0 ? t('noCredit') : credit < 0 ? t('creditPositive') : t('credit')}
                                    count={credit ? format$Price(credit < 0 ? -credit : credit || 0) : undefined}
                                    noShowCount={!Boolean(credit)}
                                />
                            )}
                        </div>
                    </div>
                    <div className="warehouse-header-toolbar">
                        <WarehouseToolBar  
                            wId={wId}
                            setPrevFilter={setPrevFilter}
                            setShowFilter={setShowFilter}
                            setShowComing={setShowComing}
                            setShowTransfer={setShowTransfer}
                            teamId={teamId}
                            teamName={teamName}
                            teamDeleted={teamDeleted}
                            customId={customId}
                            customName={customName}
                            customDeleted={customDeleted}
                            warehouseEmpty={warehouseEmpty}
                            warehouseIdApi={warehouseInfo?.warehouseIdApi || null}
                            reservObjectId={warehouseInfo?.reservObjectId || null}
                            onTeamChanged={handleTeamChanged}/>
                    </div>
                </div>
            </Col>
 
            <Col sm={12}>
                {stocksList
                    ? (
                        <WarehouseTable
                            wId={wId}
                            setShowComing={setShowComing}
                            setShowPaymentsId={setShowPaymentsId}
                            setShowPaymentsTitle={setShowPaymentsTitle}
                            setShowTransfer={setShowTransfer}
                            onlyView={!canOps}
                            stocksList={stocksList}/>)
                    : (<ESpinner/>)}
            </Col>
        </Row> 
    </>)
}    

export default Warehouse;